/**
 * Outlook Add-in Width Management Utility
 * Handles dynamic width adjustment for different Outlook clients
 */

class OutlookWidthManager {
  constructor() {
    this.minWidth = 420;
    this.preferredWidth = 500;
    this.maxWidth = 800;
    this.initialized = false;
  }

  /**
   * Initialize width management
   */
  init() {
    if (this.initialized) return;
    
    // Wait for Office.js to be ready
    if (typeof Office !== 'undefined' && Office.context) {
      this.setupWidthControl();
    } else {
      // Fallback for when Office.js isn't available
      document.addEventListener('DOMContentLoaded', () => {
        this.setupWidthControl();
      });
    }
    
    this.initialized = true;
  }

  /**
   * Set up width control based on Outlook client
   */
  setupWidthControl() {
    // Detect Outlook client type
    const clientInfo = this.detectOutlookClient();
    console.log('Detected Outlook client:', clientInfo);

    // Apply width settings based on client
    this.applyWidthSettings(clientInfo);

    // Set up responsive behavior
    this.setupResponsiveWidth();

    // Monitor for layout changes
    this.monitorLayoutChanges();
  }

  /**
   * Detect which Outlook client is being used
   */
  detectOutlookClient() {
    const userAgent = navigator.userAgent;
    const hostname = window.location.hostname;
    
    let client = {
      type: 'unknown',
      isWeb: false,
      isDesktop: false,
      isMobile: false,
      supportsFixedWidth: false
    };

    // Office.js context detection
    if (typeof Office !== 'undefined' && Office.context) {
      const platform = Office.context.platform;
      const host = Office.context.host;
      
      if (platform === Office.PlatformType.PC || platform === Office.PlatformType.Mac) {
        client.type = 'desktop';
        client.isDesktop = true;
        client.supportsFixedWidth = true;
      } else if (platform === Office.PlatformType.OfficeOnline) {
        client.type = 'web';
        client.isWeb = true;
        client.supportsFixedWidth = false;
      }
    }

    // Fallback detection based on user agent and URL
    if (client.type === 'unknown') {
      if (userAgent.includes('Outlook') && !userAgent.includes('Mobile')) {
        client.type = 'desktop';
        client.isDesktop = true;
        client.supportsFixedWidth = true;
      } else if (hostname.includes('outlook.office.com') || hostname.includes('outlook.live.com')) {
        client.type = 'web';
        client.isWeb = true;
        client.supportsFixedWidth = false;
      } else if (userAgent.includes('Mobile') || userAgent.includes('iPhone') || userAgent.includes('Android')) {
        client.type = 'mobile';
        client.isMobile = true;
        client.supportsFixedWidth = false;
      }
    }

    return client;
  }

  /**
   * Apply width settings based on client type
   */
  applyWidthSettings(clientInfo) {
    const elements = [document.documentElement, document.body];
    const app = document.getElementById('app');
    if (app) elements.push(app);

    if (clientInfo.isDesktop && clientInfo.supportsFixedWidth) {
      // Desktop Outlook - can use fixed width
      this.setFixedWidth(elements, this.preferredWidth);
    } else if (clientInfo.isWeb) {
      // Web Outlook - use responsive width with constraints
      this.setResponsiveWidth(elements);
    } else if (clientInfo.isMobile) {
      // Mobile Outlook - full width but with minimum
      this.setMobileWidth(elements);
    } else {
      // Fallback - safe responsive approach
      this.setResponsiveWidth(elements);
    }
  }

  /**
   * Set fixed width for desktop clients
   */
  setFixedWidth(elements, width) {
    elements.forEach(el => {
      if (el) {
        el.style.width = `${width}px`;
        el.style.minWidth = `${width}px`;
        el.style.maxWidth = `${this.maxWidth}px`;
      }
    });
  }

  /**
   * Set responsive width for web clients
   */
  setResponsiveWidth(elements) {
    elements.forEach(el => {
      if (el) {
        el.style.width = '100%';
        el.style.minWidth = `${this.minWidth}px`;
        el.style.maxWidth = `${this.maxWidth}px`;
      }
    });
  }

  /**
   * Set mobile-optimized width
   */
  setMobileWidth(elements) {
    elements.forEach(el => {
      if (el) {
        el.style.width = '100%';
        el.style.minWidth = '320px';
        el.style.maxWidth = '100%';
      }
    });
  }

  /**
   * Set up responsive width behavior
   */
  setupResponsiveWidth() {
    // Handle window resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.handleResize();
      }, 250);
    });

    // Handle orientation change on mobile
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        this.handleResize();
      }, 100);
    });
  }

  /**
   * Handle resize events
   */
  handleResize() {
    const clientInfo = this.detectOutlookClient();
    this.applyWidthSettings(clientInfo);
  }

  /**
   * Monitor for layout changes from Outlook
   */
  monitorLayoutChanges() {
    // Use ResizeObserver if available
    if (window.ResizeObserver) {
      const observer = new ResizeObserver(entries => {
        for (let entry of entries) {
          if (entry.target === document.body) {
            this.handleResize();
          }
        }
      });
      observer.observe(document.body);
    }

    // Fallback: check for task pane pinning changes
    if (typeof Office !== 'undefined' && Office.ribbon && Office.ribbon.requestUpdate) {
      setInterval(() => {
        this.checkPinningState();
      }, 1000);
    }
  }

  /**
   * Check if task pane is pinned (affects available width)
   */
  checkPinningState() {
    // This is a placeholder - actual implementation would depend on
    // Office.js APIs for detecting pinned state
    const currentWidth = window.innerWidth;
    if (currentWidth !== this.lastWidth) {
      this.lastWidth = currentWidth;
      this.handleResize();
    }
  }

  /**
   * Manually set width (for testing or specific scenarios)
   */
  setWidth(width) {
    if (width < this.minWidth) {
      console.warn(`Width ${width} is below minimum ${this.minWidth}px`);
      width = this.minWidth;
    }
    
    if (width > this.maxWidth) {
      console.warn(`Width ${width} exceeds maximum ${this.maxWidth}px`);
      width = this.maxWidth;
    }

    const elements = [document.documentElement, document.body];
    const app = document.getElementById('app');
    if (app) elements.push(app);

    this.setFixedWidth(elements, width);
  }

  /**
   * Get current effective width
   */
  getCurrentWidth() {
    return {
      window: window.innerWidth,
      document: document.documentElement.clientWidth,
      body: document.body.clientWidth
    };
  }
}

// Create global instance
window.OutlookWidthManager = new OutlookWidthManager();

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.OutlookWidthManager.init();
  });
} else {
  window.OutlookWidthManager.init();
}

export default window.OutlookWidthManager;
